import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";
import ReactMarkdown from "react-markdown";
import { useTheme } from "../../../context/theme";

// Ample Hire AI Assistant — split-pane chat with persistent history.
// Sessions are saved server-side and scoped to the logged-in user via JWT.

const DEFAULT_SUGGESTION_GROUPS = [
  {
    title: "Candidates",
    color: "text-vf-primary",
    items: [
      { text: "Find React developers with 5+ years in Bangalore", icon: "search" },
      { text: "Show me all candidates with Java and Spring Boot skills", icon: "search" },
      { text: "Who are the top-scored candidates this month?", icon: "star" },
      { text: "Find candidates available with less than 30 days notice period", icon: "clock" },
    ],
  },
  {
    title: "Jobs & Interviews",
    color: "text-green-600",
    items: [
      { text: "Create a Senior Backend Engineer job opening for Bangalore", icon: "briefcase" },
      { text: "Send interview invites to all React candidates for the Frontend role", icon: "send" },
      { text: "Show interview results for the DevOps/SRE position", icon: "chart" },
      { text: "What's the average score for Senior React Developer interviews?", icon: "chart" },
    ],
  },
  {
    title: "Questions & Setup",
    color: "text-amber-600",
    items: [
      { text: "Add 5 system design questions for senior engineers", icon: "layers" },
      { text: "Generate 10 React interview questions for mid-level developers", icon: "layers" },
      { text: "List all team members in my organization", icon: "users" },
      { text: "Show me the hiring funnel for this quarter", icon: "chart" },
    ],
  },
];

// Flatten for random picking
const ALL_SUGGESTIONS = DEFAULT_SUGGESTION_GROUPS.flatMap((g) => g.items);

const CATEGORY_COLORS = {
  "Candidates & Insights": "text-vf-primary",
  "Jobs & Hiring": "text-green-600",
  "Questions & Setup": "text-amber-600",
};
const CATEGORY_ICON_DEFAULT = "search";

const ICON_MAP = {
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  search: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  ),
  briefcase: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  layers: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  send: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  star: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  chart: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  clock: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
};

/* ── Animation Variants ── */
const slideInUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.04 } },
};

const staggerItem = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.2 },
};

const dotPulse = (delay) => ({
  initial: { scale: 0.6, opacity: 0.3 },
  animate: {
    scale: [0.6, 1, 0.6],
    opacity: [0.3, 1, 0.3],
    transition: { duration: 1.2, repeat: Infinity, delay, ease: "easeInOut" },
  },
});

/* ── Main Component ── */
// embedded: when true, the Assistant fills its parent (used by AssistantFab's
// modal). When false (the standalone /admin/assistant route), it occupies the
// page minus the admin chrome via calc(100vh - 140px), and draws its own
// rounded card border. We don't want that border inside the modal because the
// modal panel already provides one.
const Assistant = ({ embedded = false }) => {
  const { isDark } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [suggestionGroups, setSuggestionGroups] = useState(DEFAULT_SUGGESTION_GROUPS);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { document.title = "AI Assistant - Ample Hire"; loadSessions(); loadSuggestions(); }, []);

  const loadSuggestions = async () => {
    try {
      const { data } = await axiosInstance.post("/job-posting/api/suggestions/list", { surface: "ASSISTANT" });
      const cats = data?.data?.categories;
      if (Array.isArray(cats) && cats.length > 0) {
        const mapped = cats.map((c) => ({
          title: c.name,
          color: CATEGORY_COLORS[c.name] || "text-vf-primary",
          items: (c.prompts || []).map((p) => ({ text: p.promptText, icon: p.icon || CATEGORY_ICON_DEFAULT })),
        }));
        setSuggestionGroups(mapped);
      }
    } catch (e) { /* keep fallback */ }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + "px";
    }
  }, [input]);

  const loadSessions = async () => {
    try {
      const res = await axiosInstance.get("/ai/chat/sessions");
      setSessions(res.data || []);
    } catch (e) {
      // silent — sidebar will just be empty
    }
  };

  const openSession = async (id) => {
    if (id === activeId) return;
    setActiveId(id);
    setMessages([]);
    setInput("");
    setError(null);
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/ai/chat/sessions/${id}`);
      setMessages(res.data.messages || []);
    } catch (e) {
      setError(e?.message || "Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setInput("");
    setError(null);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await axiosInstance.delete(`/ai/chat/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (id === activeId) newChat();
    } catch (e) {
      setError("Failed to delete");
    }
  };

  const send = async (text) => {
    const prompt = (text ?? input).trim();
    if (!prompt || sending) return;
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: prompt }]);
    setInput("");
    setSending(true);

    try {
      const res = await axiosInstance.post("/ai/chat", { sessionId: activeId, message: prompt });
      const data = res.data;
      // Check for 200-with-error-in-reply (backend returns error as assistant message)
      const isErrorReply = data.reply && data.reply.startsWith("I'm sorry, I encountered an error");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.reply,
          trace: data.trace,
          isError: isErrorReply,
        },
      ]);
      if (!activeId && data.sessionId) {
        setActiveId(data.sessionId);
      }
      loadSessions();
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Something went wrong";
      // Keep the user message, add a visible error message instead of removing
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: errMsg, isError: true },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const [showHistory, setShowHistory] = useState(true);

  return (
    <div
      className={`flex overflow-hidden bg-white ${embedded ? "h-full w-full" : "rounded-card border border-subtle"}`}
      style={embedded ? undefined : { height: "calc(100vh - 140px)" }}
    >
      {/* ═══ Sessions Sidebar ═══ */}
      <AnimatePresence initial={false}>
        {showHistory && (
      <motion.aside
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 280, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="flex-shrink-0 flex flex-col bg-vf-base overflow-hidden"
      >
        {/* New Chat Button */}
        <div className="p-3.5" style={{ borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>
          <button
            onClick={newChat}
            className="w-full flex items-center justify-center gap-2 text-[13px] font-semibold py-2.5 px-4 rounded-xl text-white transition-all"
            style={{ background: "rgba(99,102,241,0.8)" }}
            onMouseEnter={(e) => e.target.style.background = "rgba(99,102,241,1)"}
            onMouseLeave={(e) => e.target.style.background = "rgba(99,102,241,0.8)"}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New chat
          </button>
        </div>

        {/* Session List */}
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length === 0 && (
            <div className="text-center py-8 px-4 text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.35)" }}>
              No conversations yet.
              <br />
              Start a new chat above.
            </div>
          )}
          <motion.div variants={staggerContainer} initial="initial" animate="animate">
            {sessions.map((s) => (
              <SessionItem
                key={s.id}
                session={s}
                isActive={s.id === activeId}
                onOpen={() => openSession(s.id)}
                onDelete={(e) => deleteSession(s.id, e)}
              />
            ))}
          </motion.div>
        </div>

        {/* Footer */}
        <div className="py-2.5 px-3.5 text-center text-[10px] tracking-wide" style={{ borderTop: "0.5px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" }}>
          Bound by your role permissions
        </div>
      </motion.aside>
        )}
      </AnimatePresence>

      {/* ═══ Chat Panel ═══ */}
      <section className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="py-3 px-6 flex items-center gap-4 bg-vf-base">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/10 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <path d="M12 19v3" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[15px] font-bold text-white m-0 leading-tight">
              Ample Hire AI
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-flex items-center text-[9px] font-semibold px-1.5 py-px rounded-full bg-green-500/20 text-green-300">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1 animate-pulse" />
                Online
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              onClick={() => setShowHistory(!showHistory)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style={{ background: "rgba(255,255,255,0.1)" }}
              title={showHistory ? "Hide history" : "Show history"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                {showHistory ? (
                  <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></>
                ) : (
                  <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><path d="M14 9l3 3-3 3"/></>
                )}
              </svg>
            </motion.button>
          </div>
        </header>

        {/* Messages Area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-6 bg-vf-bg"
        >
          {loading ? (
            <div className="text-center mt-12 text-[13px] text-vf-muted font-body">
              Loading conversation...
            </div>
          ) : messages.length === 0 ? (
            <EmptyState onPick={send} groups={suggestionGroups} />
          ) : (
            <div className="max-w-[900px] mx-auto">
              <AnimatePresence mode="popLayout">
                {messages.map((m, i) => (
                  <MessageBubble key={i} index={i} message={m} />
                ))}
              </AnimatePresence>
              {sending && <TypingIndicator />}
              {error && (
                <motion.div
                  {...slideInUp}
                  className="text-[13px] rounded-card px-3.5 py-2.5 mt-3 text-vf-danger bg-vf-danger/10 border border-vf-danger/20 font-body"
                >
                  {error}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Input Bar */}
        <div className="px-6 pt-4 pb-3 bg-white" style={{ borderTop: "0.5px solid rgba(99,102,241,0.1)" }}>
          <motion.div
            animate={{
              boxShadow: inputFocused
                ? "0 0 0 2px rgba(99, 102, 241, 0.3), 0 0 20px rgba(99, 102, 241, 0.15), 0 0 40px rgba(99, 102, 241, 0.05)"
                : "0 1px 6px rgba(99, 102, 241, 0.06)",
              borderColor: inputFocused ? "rgba(99, 102, 241, 0.4)" : "rgba(99, 102, 241, 0.12)",
            }}
            transition={{ duration: 0.3 }}
            className="max-w-[900px] mx-auto flex items-end gap-2 rounded-2xl p-2 pl-5 bg-white border"
          >
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder="Ask me anything — search candidates, create jobs, send invites..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              disabled={sending}
              className="flex-1 resize-none border-none outline-none py-2 text-sm leading-relaxed text-vf-base bg-transparent placeholder-vf-muted min-h-[40px] max-h-[160px]"
              style={{ opacity: sending ? 0.5 : 1 }}
            />
            <motion.button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary flex-shrink-0 flex items-center justify-center w-10 h-10 p-0 rounded-xl disabled:opacity-30"
            >
              {sending ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full inline-block"
                />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
                </svg>
              )}
            </motion.button>
          </motion.div>
          <div className="max-w-[900px] mx-auto flex items-center justify-between mt-2 px-1">
            <p className="text-[10px] text-vf-muted">
              Press <kbd className="px-1 py-0.5 rounded bg-vf-tint text-vf-muted text-[9px]">Enter</kbd> to send · <kbd className="px-1 py-0.5 rounded bg-vf-tint text-vf-muted text-[9px]">Shift+Enter</kbd> for new line
            </p>
            <p className="text-[10px] text-vf-muted">AI may produce incorrect responses</p>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ── Session Item ── */
const SessionItem = ({ session, isActive, onOpen, onDelete }) => (
  <motion.button
    variants={staggerItem}
    onClick={onOpen}
    whileHover={{ x: 2 }}
    transition={{ duration: 0.15 }}
    className={`w-full text-left px-3 py-2.5 rounded-xl flex items-start gap-2.5 cursor-pointer mb-0.5 group transition-all border ${
      isActive
        ? "bg-vf-primary text-white border-vf-primary"
        : "border-transparent hover:bg-white/5"
    }`}
    style={{ color: isActive ? undefined : "rgba(255,255,255,0.65)" }}
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`mt-0.5 flex-shrink-0 ${isActive ? "text-white opacity-80" : "opacity-40"}`}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
    <span
      className={`flex-1 text-[13px] truncate ${
        isActive ? "text-white font-semibold" : "font-normal"
      }`}
    >
      {session.title}
    </span>
    <span
      role="button"
      onClick={onDelete}
      title="Delete"
      className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
        isActive ? "text-white/70 hover:text-white" : "hover:text-red-400"
      }`}
      style={{ color: isActive ? undefined : "rgba(255,255,255,0.3)" }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      </svg>
    </span>
  </motion.button>
);

/* ── Empty State ── */
const EmptyState = ({ onPick, groups }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="max-w-[640px] mx-auto mt-8 mb-6"
  >
    {/* Header */}
    <div className="text-center mb-8">
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-vf-primary/10 text-vf-primary"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <path d="M12 19v3" />
        </svg>
      </motion.div>
      <h2 className="text-[22px] font-bold text-vf-base mb-1.5">How can I help today?</h2>
      <p className="text-sm text-vf-muted">Ask about candidates, jobs, interviews, or let me handle tasks for you.</p>
    </div>

    {/* Suggestion Groups */}
    <div className="space-y-5">
      {(groups || DEFAULT_SUGGESTION_GROUPS).map((group, gi) => (
        <motion.div
          key={group.title}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 + gi * 0.1 }}
        >
          <div className={`text-2xs font-semibold uppercase tracking-widest mb-2 ${group.color}`}>
            {group.title}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {group.items.map((s, i) => (
              <motion.button
                key={s.text}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.2 + gi * 0.1 + i * 0.04 }}
                whileHover={{ y: -1, backgroundColor: "rgba(99,102,241,0.06)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onPick(s.text)}
                className="text-left p-3 rounded-xl border border-subtle bg-white hover:border-vf-primary/30 cursor-pointer flex items-start gap-2.5 transition-all"
              >
                <span className="flex-shrink-0 mt-0.5 text-vf-primary opacity-60">
                  {ICON_MAP[s.icon]}
                </span>
                <span className="text-xs text-vf-base leading-snug">{s.text}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  </motion.div>
);

/* ── Message Bubble ── */
const MessageBubble = ({ message, index }) => {
  const isUser = message.role === "user";

  return (
    <motion.div
      {...slideInUp}
      transition={{ duration: 0.3, delay: 0.02 }}
      layout
      className={`flex gap-3 mb-6 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      {/* Avatar */}
      <div
        className={`w-8 h-8 flex-shrink-0 flex items-center justify-center text-[11px] font-bold font-heading ${
          isUser
            ? "rounded-card bg-vf-primary text-white"
            : "rounded-full bg-vf-primary/10 text-vf-primary"
        }`}
      >
        {isUser ? "You" : "AI"}
      </div>

      {/* Bubble */}
      <div
        className={`max-w-[85%] px-5 py-3.5 text-sm leading-relaxed border ${
          isUser
            ? "rounded-[14px_4px_14px_14px] bg-vf-primary/10 border-vf-primary/15 whitespace-pre-wrap text-vf-base"
            : message.isError
              ? "rounded-[4px_14px_14px_14px] bg-red-50 border-red-200 text-red-700"
              : "rounded-[4px_14px_14px_14px] bg-vf-tint border-subtle ai-message-content text-vf-base"
        }`}
      >
        {isUser ? message.content : (
          <ReactMarkdown
            components={{
              h1: ({ children }) => <h1 className="text-base font-bold text-vf-base mt-3 mb-1.5 first:mt-0">{children}</h1>,
              h2: ({ children }) => <h2 className="text-sm font-bold text-vf-base mt-3 mb-1.5 first:mt-0">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-semibold text-vf-base mt-2 mb-1 first:mt-0">{children}</h3>,
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="mb-2 pl-4 space-y-0.5 list-disc">{children}</ul>,
              ol: ({ children }) => <ol className="mb-2 pl-4 space-y-0.5 list-decimal">{children}</ol>,
              li: ({ children }) => <li className="text-sm">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-vf-base">{children}</strong>,
              em: ({ children }) => <em className="italic text-vf-muted">{children}</em>,
              code: ({ inline, children }) => inline
                ? <code className="px-1.5 py-0.5 rounded bg-vf-primary/8 text-vf-primary text-xs font-mono">{children}</code>
                : <code className="block p-3 rounded-lg bg-vf-base text-vf-highlight text-xs font-mono overflow-x-auto my-2 whitespace-pre">{children}</code>,
              pre: ({ children }) => <pre className="my-2">{children}</pre>,
              table: ({ children }) => <div className="overflow-x-auto my-2 rounded-lg border border-subtle"><table className="w-full text-xs">{children}</table></div>,
              thead: ({ children }) => <thead className="bg-vf-tint">{children}</thead>,
              th: ({ children }) => <th className="text-left px-3 py-2 font-semibold text-vf-base border-b border-subtle">{children}</th>,
              td: ({ children }) => <td className="px-3 py-2 text-vf-base border-b border-subtle">{children}</td>,
              tr: ({ children }) => <tr className="hover:bg-vf-tint/50">{children}</tr>,
              a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-vf-primary underline hover:text-vf-accent">{children}</a>,
              blockquote: ({ children }) => <blockquote className="border-l-2 border-vf-primary pl-3 my-2 text-vf-muted italic">{children}</blockquote>,
              hr: () => <hr className="my-3 border-subtle" />,
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>
    </motion.div>
  );
};

/* ── Tool Trace ── */
const ToolTrace = ({ trace }) => (
  <details className="mt-2.5 text-[11px] rounded-lg overflow-hidden bg-vf-bg border border-subtle">
    <summary className="cursor-pointer select-none px-2.5 py-1.5 flex items-center gap-1.5 font-medium font-body text-vf-primary">
      <span className="w-1.5 h-1.5 rounded-full bg-vf-primary" />
      {trace.length} tool call{trace.length > 1 ? "s" : ""}
    </summary>
    <ul className="px-2.5 pb-1.5 m-0 list-none text-vf-muted">
      {trace.map((t, i) => (
        <li
          key={i}
          className="flex items-center gap-1.5 py-0.5 font-body"
        >
          <span className="w-1 h-1 rounded-full bg-vf-muted opacity-50" />
          {t.name}
        </li>
      ))}
    </ul>
  </details>
);

/* ── Typing Indicator ── */
const TypingIndicator = () => (
  <motion.div
    {...slideInUp}
    className="flex gap-3 mb-4"
  >
    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold font-heading bg-vf-primary/10 text-vf-primary">
      AI
    </div>
    <div className="rounded-[4px_14px_14px_14px] px-4 py-3.5 flex items-center gap-1.5 bg-vf-tint border border-subtle">
      {[0, 0.15, 0.3].map((delay, i) => (
        <motion.span
          key={i}
          {...dotPulse(delay)}
          className="w-[7px] h-[7px] rounded-full bg-vf-primary block"
        />
      ))}
    </div>
  </motion.div>
);

export default Assistant;
